import {
  Edit as EditIcon,
  DeleteForever as DeleteForeverIcon,
  PersonOff as BannedIcon,
  Person as UnbannedIcon,
  LockReset as LockResetIcon,
  Login as LoginIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { MenuItem } from '@mui/material';
import moment from 'moment';
import { DataGridActionMenu, DataGridListPage } from 'pages/Common';
import React, { useMemo } from 'react';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { deleteDataGridRow, editDataGridRow } from 'store/actions';
import { pageKeys } from 'store/reducers/dataGrid.reducer';
import { ResetPasswordDialog } from './ResetPasswordDialog';
import { useAppDispatch } from 'store';
import { User } from 'constants/types';
import apiEndpoints from 'constants/endpoints';
import { apiRequest } from 'api-client';
import { setCookie } from 'utils/cookie';
import { toast } from 'react-toastify';

export const UserList = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const banUser = useCallback((id: number) => {
    dispatch(editDataGridRow(apiEndpoints.users, id, {bannedAt: new Date()}, {}, pageKeys.userList));
  }, [dispatch]);

  const unbanUser = useCallback((id: number) => {
    dispatch(editDataGridRow(apiEndpoints.users, id, {bannedAt: null}, {}, pageKeys.userList));
  }, [dispatch])

  const deleteUser = useCallback((id: number, email: string) => {
    const confirmed = window.confirm(`Are you sure you want to delete permanently the user with email ${email}? All data related to this user will be deleted!!!`);
    if (confirmed) {
      dispatch(deleteDataGridRow(apiEndpoints.users, id, {}, {}, pageKeys.userList));
    }

  }, [dispatch]);

  const loginAsUser = useCallback((id: number) => {
    const confirmed = window.confirm(`Are you sure you want to login as this user?`);
    if (!confirmed) {
      return;
    }
    apiRequest.post(`${apiEndpoints.users}/${id}/token`).then((data: { token: string }) => {
      const { token } = data;
      // Set cookie for parent domain with 1 day expiration
      const expirationTime = Date.now() + (1 * 24 * 60 * 60 * 1000);
      setCookie('access_token', token, expirationTime, '.kozoom.com', '/');
      window.open('https://tv.kozoom.com', '_blank');
    }).catch((error) => {
      toast.error(`Failed to login as this user: ${error.message}`);
    });
  }, []);

  const [resetPasswordUser, setResetPasswordUser] = React.useState<User | null>(null);

  const columns = useMemo(() => {

    return [
      {
        field: 'id',
        headerName: 'Id',
        width: 80,
      },
      {
        field: 'firstName',
        headerName: 'First Name',
        width: 180,
      },
      {
        field: 'lastName',
        headerName: 'Last Name',
        width: 180,
      },
      {
        field: 'email',
        headerName: 'Email',
        width: 250,
      },
      {
        field: 'isAdmin',
        headerName: 'Admin',
        width: 90,
        valueGetter: ({ data }: { data: User & { is_admin?: boolean; isIsAdmin?: boolean } }) =>
          data?.isAdmin ?? data?.is_admin ?? data?.isIsAdmin,
        cellRenderer: ({ value }: { value: boolean | undefined }) =>
          value ? <CheckIcon color="success" /> : null,
      },
      {
        field: 'lastLoginAt',
        headerName: 'Last Login',
        width: 150,
        valueFormatter: ({ value }: {value: string | null}) => value && moment(value).format('Y-MM-DD HH:mm'),
      },
      {
        field: 'bannedAt',
        headerName: 'Banned At',
        width: 100,
        valueFormatter: ({ value }: {value: string | null}) => value && moment(value).format('Y-MM-DD'),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 1,
        cellStyle: {textAlign: 'center'},
        headerClass: 'text-center',
        cellRenderer: ({ data }: {data: User}) => (
          <>
            <DataGridActionMenu>
              <MenuItem onClick={() => history.push(`/${apiEndpoints.users}/${data.id}`)}><EditIcon />Edit</MenuItem>
              <MenuItem onClick={() => setResetPasswordUser(data)}><LockResetIcon />Reset Password</MenuItem>
              <MenuItem onClick={() => deleteUser(data.id, data.email)}><DeleteForeverIcon />Delete</MenuItem>
              {data.bannedAt &&
                <MenuItem onClick={() => unbanUser(data.id)}><UnbannedIcon />Unban User</MenuItem>
              }
              {!data.bannedAt &&
                <MenuItem onClick={() => banUser(data.id)}><BannedIcon />Ban User</MenuItem>
              }
              <MenuItem onClick={() => loginAsUser(data.id)}><LoginIcon /> Login as this user</MenuItem>
            </DataGridActionMenu>
          </>
        ),
      },
    ]
  }, [history, banUser, unbanUser, deleteUser]);
  const filters = [
    {
      field: 'searchStr',
      label: 'Search',
      type: 'search',
      value: null
    },
    {
      field: 'isBanned',
      label: 'Is Banned',
      type: 'switch',
      value: 0,
      dataType: 'int'
    },
    {
      field: 'isAdmin',
      label: 'Admin only',
      type: 'switch',
      value: 0,
      dataType: 'int'
    },
  ];
  const gridOptions = {
    rowClassRules: {
      "highlight-row": "data.bannedAt"
    }
  }

  return (
    <>
      <DataGridListPage
        columns={columns as any}
        filters={filters as any}
        apiEndpoint={apiEndpoints.users}
        heading="Users"
        pageKey={pageKeys.userList}
        gridOptions={gridOptions}
        onAdd={undefined}
        customHeadingToolbar={undefined}
      />
      <ResetPasswordDialog
        user={resetPasswordUser}
        onClose={() => setResetPasswordUser(null)}
      />
    </>
  );
}
